import { useRef, useEffect, useState, type KeyboardEvent } from 'react'
import dayjs from 'dayjs'
import { useHistory } from 'react-router-dom'
import { Text, SearchInput, Flex, spacing } from '@retailer-platform/shared-components/src/tds'
import { useDebouncedState, colors } from '@retailer-platform/shared-components'
import { UserIcon, CartIcon } from '@instacart/ids-core'
import { useDomainMessages } from '../utils/domain/intl'
import { useFindUsers } from '../utils/hooks/useFindUsers.hooks'
import { useGetOrderByIdBasic } from '../utils/hooks/useGetOrderByIdBasic.hooks'
import { buildCustomerUrl, buildOrderUrl } from '../utils/domain/routing'
import CategorizedResultsDropdown from './CategorizedResultsDropdown'

interface CustomerOrderSearchInputProps {
  onSearch: (query: string) => void
  onNavigate?: () => void
  onQueryChange?: (query: string) => void
  placeholder?: string
  labelProps?: { label: string }
  initialValue?: string
  autoFocus?: boolean
  enterGoesToFirstResult?: boolean
}

const CustomerOrderSearchInput = ({
  onSearch,
  onNavigate,
  onQueryChange,
  placeholder,
  labelProps,
  initialValue,
  autoFocus,
  enterGoesToFirstResult = false,
}: CustomerOrderSearchInputProps) => {
  const [query, debouncedQuery, setQuery] = useDebouncedState(initialValue ?? '', 300)
  const history = useHistory()

  const messages = useDomainMessages({
    searchResultsHeader: 'customersAndOrdersDomain.admin.customersAndOrders.searchResultsHeader',
    searchNoResults: 'customersAndOrdersDomain.admin.customersAndOrders.searchNoResults',
    ordersResultsHeader: 'customersAndOrdersDomain.admin.customersAndOrders.ordersResultsHeader',
    ordersNoResults: 'customersAndOrdersDomain.admin.customersAndOrders.ordersNoResults',
    orderLabel: 'customersAndOrdersDomain.admin.customersAndOrders.orderLabel',
    orderArriving: 'customersAndOrdersDomain.admin.customersAndOrders.orderArriving',
  })

  const numericQuery = /^\d+$/.test(debouncedQuery.trim()) ? debouncedQuery.trim() : null
  const { data: users, loading: usersLoading } = useFindUsers(debouncedQuery)
  const { data: order, loading: orderLoading } = useGetOrderByIdBasic(numericQuery)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [dropdownDismissed, setDropdownDismissed] = useState(false)

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  useEffect(() => {
    if (initialValue !== undefined) setQuery(initialValue)
  }, [initialValue, setQuery])

  useEffect(() => {
    setDropdownDismissed(false)
  }, [debouncedQuery])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownDismissed(true)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleChange = (value: string) => {
    setQuery(value)
    onQueryChange?.(value)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const trimmed = query.trim()
      if (!trimmed) return
      if (enterGoesToFirstResult) {
        const firstUser = users?.[0]
        if (firstUser?.id) {
          onNavigate?.()
          history.push(buildCustomerUrl(String(firstUser.id)))
        }
      } else {
        onSearch(trimmed)
      }
    }
  }

  const showDropdown = debouncedQuery.trim().length > 0 && !dropdownDismissed

  return (
    <div css={{ flex: 1, position: 'relative' }} ref={containerRef}>
      <SearchInput
        ref={inputRef}
        placeholder={placeholder}
        labelProps={labelProps}
        value={query}
        onChange={evt => handleChange(evt.target.value)}
        onFocus={() => setDropdownDismissed(false)}
        onKeyDown={handleKeyDown}
        onClear={() => handleChange('')}
        width="full"
      />
      {showDropdown && (
        <div
          css={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            maxWidth: '628px',
            marginTop: spacing.s4,
            zIndex: 700,
          }}
        >
          <CategorizedResultsDropdown
            loading={usersLoading || orderLoading}
            sections={[
              {
                title: messages.searchResultsHeader,
                icon: <UserIcon size={16} />,
                emptyLabel: messages.searchNoResults,
                items: (users ?? []).map(user => (
                  <Flex.Column
                    key={String(user?.id)}
                    onClick={() => {
                      onNavigate?.()
                      history.push(buildCustomerUrl(String(user?.id)))
                    }}
                    css={{
                      padding: `${spacing.s8} ${spacing.s16}`,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: colors.GRAYSCALE.X10 },
                      borderBottom: `1px solid ${colors.GRAYSCALE.X10}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Text typography="bodyEmphasized" css={{ marginBottom: '0 !important' }}>
                      {[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
                    </Text>
                    <Text typography="bodyRegular" color="systemGrayscale60">
                      {[user?.phone, user?.email].filter(Boolean).join(' • ')}
                    </Text>
                  </Flex.Column>
                )),
              },
              ...(numericQuery
                ? [
                    {
                      title: messages.ordersResultsHeader,
                      icon: <CartIcon size={16} />,
                      emptyLabel: messages.ordersNoResults,
                      items: order
                        ? [
                            <Flex.Column
                              key={order.id}
                              onClick={() => {
                                onNavigate?.()
                                history.push(buildOrderUrl(String(order.userId), String(order.id)))
                              }}
                              css={{
                                padding: `${spacing.s8} ${spacing.s16}`,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: colors.GRAYSCALE.X10 },
                              }}
                            >
                              <Text
                                typography="bodyEmphasized"
                                css={{ marginBottom: '0 !important' }}
                              >
                                {messages.orderLabel} {order.id}
                              </Text>
                              <Text typography="bodyRegular" color="systemGrayscale60">
                                {[
                                  order.userFullName,
                                  order.fulfillmentDate
                                    ? `${messages.orderArriving} ${dayjs(order.fulfillmentDate).format('M/DD/YYYY [by] ha')}`
                                    : null,
                                  order.serviceType
                                    ?.replace(/^SERVICE_TYPE_/, '')
                                    .toLowerCase()
                                    .replace(/^\w/, c => c.toUpperCase()),
                                ]
                                  .filter(Boolean)
                                  .join(' • ')}
                              </Text>
                            </Flex.Column>,
                          ]
                        : [],
                    },
                  ]
                : []),
            ]}
          />
        </div>
      )}
    </div>
  )
}

export default CustomerOrderSearchInput
